import { Box, SvgIcon, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import {
  selectAlerts,
  selectProductDetail,
  selectProfitCalculator,
  selectQuickInfo,
  selectRankAndPrice,
} from "../../../../../features/productAnalyzer/productAnalyzer.selector";
import { formatNumber, isNumber, toNumber } from "../../../../../helpers";
import { useSelector } from "react-redux";
import { updateLookupThunk } from "../../../../../features/productAnalyzer/productAnalyzer.thunk";
import ProductValue from "../../common/ProductValue/index";
import EditDocumentIcon from "@mui/icons-material/EditDocument";
import { Tooltip } from "../../../../../components";
import { renderSizeColor } from "../../../partials/Alerts/utils";
import DetailRow from "../../common/DetailRow";
import DetailGrid from "../../common/DetailGrid";
import NumberInput from "../../common/NumberInput";
import { colors } from "../../config";

export default function ProductDetail() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { productDetail } = useAppSelector(selectProductDetail);
  const { ranks: currentRanks } = useAppSelector((state) =>
    selectRankAndPrice(state, "Current"),
  );
  const { ranks: ranks30 } = useAppSelector((state) =>
    selectRankAndPrice(state, "Days30"),
  );
  const { ranks: ranks90 } = useAppSelector((state) =>
    selectRankAndPrice(state, "Days90"),
  );
  const { ranks: ranks180 } = useAppSelector((state) =>
    selectRankAndPrice(state, "Days180"),
  );
  const keepaBSRDrops = `${ranks30?.keepaBSRDrops || "-"} | ${ranks90?.keepaBSRDrops || "-"} | ${ranks180?.keepaBSRDrops || "-"}`;
  const { quickInfo } = useAppSelector(selectQuickInfo);
  const bsr: number =
    quickInfo && isNumber(quickInfo.bsr) ? Number(quickInfo.bsr) : -1;
  const rankPercent: number =
    bsr > 0 && toNumber(quickInfo?.categoryProductCount) > 0
      ? (bsr / toNumber(quickInfo?.categoryProductCount)) * 100
      : 0;
  const { alerts } = useAppSelector(selectAlerts);
  const estimatedSales: number = currentRanks?.estimatedSales || 0;
  const { profitCalculator } = useSelector(selectProfitCalculator);

  return (
    <Box sx={{ pt: 1.5, px: 0.75, mb: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          rowGap: 1,
          columnGap: 2,
          width: "100%",
          [theme.breakpoints.up(400)]: {
            gridTemplateColumns: "1fr 1fr",
            gap: 7.5,
          },
        }}
      >
        <DetailGrid>
          <DetailRow label="ASIN">
            <ProductValue text={productDetail?.asin} arrowDown={false} />
          </DetailRow>
          <DetailRow label="Brand">
            <ProductValue
              text={productDetail?.manufacturer}
              arrowDown={false}
            />
          </DetailRow>
          <DetailRow label="Drops">
            <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
              {keepaBSRDrops !== "- | - | -" ? keepaBSRDrops : "-"}
            </Typography>
          </DetailRow>
          <DetailRow label="BSR">
            <Typography variant="body1">{bsr > 0 ? bsr : "-"}</Typography>
          </DetailRow>
          <DetailRow label="Rank">
            <Typography variant="body1">
              {rankPercent > 0 ? `${rankPercent.toFixed(2)}%` : "-"}
            </Typography>
          </DetailRow>
          <DetailRow label="Category">
            <ProductValue text={productDetail?.category} arrowDown={false} />
          </DetailRow>
        </DetailGrid>
        <DetailGrid>
          <DetailRow label="UPC">
            <ProductValue text={productDetail?.upc} arrowDown={true} />
          </DetailRow>
          <DetailRow label="EAN">
            <ProductValue text={productDetail?.ean} arrowDown={true} />
          </DetailRow>
          <DetailRow label="Model">
            <ProductValue text={productDetail?.model} arrowDown={true} />
          </DetailRow>
          <DetailRow label="Size">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="body1"
                sx={{ color: renderSizeColor(alerts) }}
              >
                {alerts?.size || "-"}
              </Typography>
              {productDetail?.packageDimensions && (
                <Tooltip
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="subtitle1" component="strong">
                        Package Dimensions
                      </Typography>
                      <Typography variant="body2">
                        Width: {productDetail?.packageDimensions?.width || "-"}
                      </Typography>
                      <Typography variant="body2">
                        Height:{" "}
                        {productDetail?.packageDimensions?.height || "-"}
                      </Typography>
                      <Typography variant="body2">
                        Length:{" "}
                        {productDetail?.packageDimensions?.length || "-"}
                      </Typography>
                      <Typography variant="body2">
                        Weight:{" "}
                        {productDetail?.packageDimensions?.weight || "-"}
                      </Typography>
                    </Box>
                  }
                >
                  <SvgIcon
                    component={EditDocumentIcon}
                    sx={{
                      fontSize: "14px",
                      verticalAlign: "middle",
                      color: colors.primary.main,
                      cursor: "pointer",
                      "&:hover": {
                        color: colors.accent.orange,
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </DetailRow>
          <DetailRow label="Est. Sales">
            <Typography variant="body1">
              {estimatedSales > 0
                ? `${formatNumber(estimatedSales, { forcePlusSuffix: true })}/mo`
                : "-"}
            </Typography>
          </DetailRow>
          <DetailRow label="Multi-pack">
            <NumberInput
              value={profitCalculator.multipackQuantity}
              onChange={(value) => {
                dispatch(
                  updateLookupThunk({
                    multipackQuantity: value,
                  }),
                );
              }}
              min={1}
            />
          </DetailRow>
        </DetailGrid>
      </Box>
    </Box>
  );
}
